import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	cn,
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandEmpty,
} from "@dune/ui";
import { Check, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useUpdateParticipantInsurer } from "../../../../api/queries/contractors.queries";
import { useGetAllInsurers } from "../../../../api/queries/insurers-admin.queries";

interface InsurerSociety {
	id: number;
	denomination: string;
}

interface InsurerSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	participantId: number | null;
	constructionSiteId: number | null;
	currentInsurer: InsurerSociety | null;
	onSuccess?: () => void;
}

export const InsurerSelectionModal: React.FC<InsurerSelectionModalProps> = ({
	isOpen,
	onClose,
	participantId,
	constructionSiteId,
	currentInsurer,
	onSuccess,
}) => {
	const [search, setSearch] = useState("");
	const [selectedInsurer, setSelectedInsurer] = useState<InsurerSociety | null>(
		null,
	);

	useEffect(() => {
		setSelectedInsurer(currentInsurer);
	}, [currentInsurer]);

	const { data: insurersData, isLoading } = useGetAllInsurers({
		search: search || undefined,
	});
	const insurers = insurersData?.data ?? [];
	const { mutateAsync: updateParticipant, isPending } =
		useUpdateParticipantInsurer();

	const handleSave = async () => {
		const insurerSocietyId = selectedInsurer?.id;
		if (!insurerSocietyId) return;

		try {
			if (participantId && constructionSiteId) {
				await updateParticipant({
					participantId,
					constructionSiteId,
					insurerSocietyId,
				});
			}
			onSuccess?.();
			onClose();
		} catch (error) {
			console.error("Erreur lors de la mise à jour de l'assureur:", error);
		}
	};

	const handleClose = () => {
		setSelectedInsurer(null);
		setSearch("");
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>
						{currentInsurer ? "Modifier l'assureur" : "Ajouter un assureur"}
					</DialogTitle>
					<DialogDescription>
						Recherchez et sélectionnez un assureur pour cet intervenant sur ce
						chantier.
					</DialogDescription>
				</DialogHeader>

				<div className="py-4">
					<Command className="border rounded-lg">
						<CommandInput
							placeholder="Rechercher un assureur..."
							value={search}
							onValueChange={setSearch}
						/>
						<CommandList>
							<CommandEmpty>
								{isLoading ? "Chargement..." : "Aucun assureur trouvé."}
							</CommandEmpty>
							<CommandGroup>
								{insurers.map((insurer) => (
									<CommandItem
										key={insurer.id}
										value={`${insurer.society.denomination} ${insurer.society.siren}`}
										onSelect={() => {
											setSelectedInsurer({
												id: insurer.id,
												denomination: insurer.society.denomination,
											});
										}}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												selectedInsurer?.id === insurer.id
													? "opacity-100"
													: "opacity-0",
											)}
										/>
										<div className="flex flex-col">
											<span className="font-medium">
												{insurer.society.denomination}
											</span>
											{insurer.society.siren && (
												<span className="text-xs text-gray-500">
													SIREN: {insurer.society.siren}
												</span>
											)}
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						Annuler
					</Button>
					<Button
						variant="outline"
						onClick={() => window.open("/insurers-management", "_blank")}
					>
						<Plus className="mr-2 h-4 w-4" />
						Ajouter un assureur
					</Button>
					<Button
						onClick={handleSave}
						disabled={!selectedInsurer?.id || isPending}
					>
						{isPending ? "Enregistrement..." : "Enregistrer"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
